import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayout } from '@/theme/useLayout';
import { THEME_STYLES, type ThemeStyle } from '@/theme/themeStorage';
import type { LayoutClass } from '@/theme/LayoutProvider';

/**
 * The layout settings drawer (`_AppSettings.cshtml`).
 *
 * Every control maps onto LayoutProvider state, which is the single writer of the
 * html class list — the same contract `smartApp.js`'s `data-action="toggle"` and
 * `data-action="theme-style"` handlers had, minus the DOM mutation.
 *
 * Two things in the Razor markup are intentionally absent: the `data-action="playsound"`
 * button (sound playback is not ported) and "Reset Panel", which reset the
 * dashboard panel drag/drop state that no page in this application uses.
 */

interface Toggle {
  id: string;
  cls: LayoutClass;
  label: string;
  fallback: string;
  /** Desktop-only rows, hidden on small screens by the original markup. */
  desktopOnly?: boolean;
}

const DESKTOP_ONLY = 'd-none d-lg-block d-xl-block d-xxl-block';

const GROUPS: { prefix: string; fallback: string; desktopOnly?: boolean; toggles: Toggle[] }[] = [
  {
    prefix: 'PrimarySettings',
    fallback: 'Primary Settings',
    toggles: [
      {
        id: 'actionHeaderFixed',
        cls: 'set-header-fixed',
        label: 'HeaderPositionFixed',
        fallback: 'Header position fixed',
      },
      {
        id: 'actionNavFull',
        cls: 'set-nav-full',
        label: 'NavigationFullHeight',
        fallback: 'Navigation full height',
        desktopOnly: true,
      },
    ],
  },
  {
    prefix: 'AddonSettings',
    fallback: 'Addon Settings',
    desktopOnly: true,
    toggles: [
      {
        id: 'actionNavFixed',
        cls: 'set-nav-fixed',
        label: 'NavigationPositionFixed',
        fallback: 'Navigation position fixed',
      },
      {
        id: 'actionNavCollapsed',
        cls: 'set-nav-collapsed',
        label: 'NavigationCollapsed',
        fallback: 'Navigation collapsed',
      },
    ],
  },
  {
    prefix: 'MiscSettings',
    fallback: 'Misc Settings',
    toggles: [
      {
        id: 'actionNavMinified',
        cls: 'set-nav-minified',
        label: 'NavigationMinified',
        fallback: 'Navigation minified',
        desktopOnly: true,
      },
      {
        id: 'darkNavigation',
        cls: 'set-nav-dark',
        label: 'DarkNavigation',
        fallback: 'Dark navigation',
      },
    ],
  },
  {
    prefix: 'AriaSettings',
    fallback: 'Aria Settings',
    toggles: [
      {
        id: 'actionColorblindMode',
        cls: 'set-colorblind-mode',
        label: 'ColorblindMode',
        fallback: 'Colorblind mode',
      },
      {
        id: 'actionHighContrastMode',
        cls: 'set-high-contrast-mode',
        label: 'HighContrastMode',
        fallback: 'High contrast mode',
      },
    ],
  },
];

/** The gradient swatches from the Razor labels, kept verbatim. */
const THEME_PREVIEWS: Record<'default' | ThemeStyle, string> = {
  default: 'linear-gradient(135deg, #FF6A00, #F6A2D5, #4C91BF, #7A8B92, #AB7C9A)',
  nebula: 'linear-gradient(135deg, #2a7dbf, #2a9d8f, #766cbc, #f4a261, #e76f51)',
  olive: 'linear-gradient(135deg, #556B2F, #6B8E23, #8B9A3D, #A9B83E, #BDB76B)',
  solar: 'linear-gradient(135deg, #FF8C00, #FFD700, #FF4500, #F1C40F, #F39C12)',
  lunar: 'linear-gradient(135deg, #2C3E50, #34495E, #5F6368, #AAB7B8, #E6E6FA, #F1F3F4)',
  night: 'linear-gradient(135deg, #1e2a47, #2b3654, #363d6c, #4f5d79, #717b91, #b6c4d1)',
  aurora: 'linear-gradient(135deg, #337e7e, #527a4a, #63279b, #7FFF00, #87CEFA, #B0E0E6)',
  earth: 'linear-gradient(135deg, #2198f3, #3173a5, #3f6888, #618d48, #52bf11)',
  flare: 'linear-gradient(135deg, #FF4500, #FF6347, #F44336, #D32F2F, #B71C1C)',
  storm: 'linear-gradient(135deg, #2F4F4F, #3B5360, #4B6A6E, #5A7980, #A9A9A9, #FFD700)',
};

/** Capitalised name shown in the swatch tooltip via `data-prefix`. */
function themeLabel(style: ThemeStyle): string {
  return style.charAt(0).toUpperCase() + style.slice(1);
}

export function AppSettings() {
  const { t } = useTranslation();
  const {
    hasLayoutClass,
    toggleLayoutClass,
    themeStyle,
    setThemeStyle,
    settingsOpen,
    setSettingsOpen,
    resetLayout,
  } = useLayout();

  return (
    <>
      <aside className={`app-drawer js-drawer-settings${settingsOpen ? ' open' : ''}`}>
        <div className="app-drawer-header">
          <div className="h4 mb-0">{t('LayoutSettings', 'Layout settings')}</div>
          <button
            type="button"
            className="btn btn-system ms-auto"
            onClick={() => setSettingsOpen(false)}
            aria-label={t('Close', 'Close')}
          >
            <svg className="sa-icon sa-icon-2x">
              <use href="/myui/icons/sprite.svg#x" />
            </svg>
          </button>
        </div>

        <div className="custom-scrollbar h-100">
          <div className="d-flex justify-content-spaced w-100 app-fob-showcase-text" data-prefix="Preview">
            <div className="app-fob app-fob-lg app-fob-showcase">
              <div />
              <div />
              <div />
            </div>
          </div>

          {GROUPS.map((group) => (
            <div
              key={group.prefix}
              className={`mod-status${group.desktopOnly ? ` ${DESKTOP_ONLY}` : ''}`}
              data-prefix={t(group.prefix, group.fallback)}
            >
              {group.toggles.map((toggle) => (
                <div
                  key={toggle.id}
                  className={`form-check${toggle.desktopOnly ? ` ${DESKTOP_ONLY}` : ''}`}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={toggle.id}
                    checked={hasLayoutClass(toggle.cls)}
                    onChange={() => toggleLayoutClass(toggle.cls)}
                  />
                  <label className="form-check-label" htmlFor={toggle.id}>
                    {t(toggle.label, toggle.fallback)}
                  </label>
                </div>
              ))}
            </div>
          ))}

          <div className="mod-status app-themes" data-prefix={t('Themes', 'Themes')}>
            <div className="clickable-boxes">
              <input
                type="radio"
                id="themeOptionDefault"
                name="options"
                checked={themeStyle === null}
                onChange={() => setThemeStyle(null)}
              />
              <label
                htmlFor="themeOptionDefault"
                data-prefix={t('Default', 'Default')}
                style={{ background: THEME_PREVIEWS.default }}
              />

              {/* A Fragment, not a wrapper element: `.clickable-boxes label` is a
                  flex item and `input:checked + label` is what colours the active
                  swatch, so nothing may come between them. */}
              {THEME_STYLES.map((style) => (
                <Fragment key={style}>
                  <input
                    type="radio"
                    id={`themeOption-${style}`}
                    name="options"
                    checked={themeStyle === style}
                    onChange={() => setThemeStyle(style)}
                  />
                  <label
                    htmlFor={`themeOption-${style}`}
                    data-prefix={themeLabel(style)}
                    style={{ background: THEME_PREVIEWS[style] }}
                  />
                </Fragment>
              ))}
            </div>
          </div>

          <div className="d-flex" style={{ gap: '10px' }}>
            <button
              type="button"
              className="btn reset-button btn-outline-danger flex-grow-1"
              onClick={resetLayout}
            >
              <i className="sa sa-reload align-base me-1" />
              {t('FactoryReset', 'Factory reset')}
            </button>
          </div>
        </div>
      </aside>

      {/* No `data-toggleclass`: that attribute is what the stylesheet uses to tell
          the mobile-menu backdrop apart from a drawer's
          (`.open + .backdrop:not([data-toggleclass="app-mobile-menu-open"])`). */}
      <button
        type="button"
        className="backdrop"
        onClick={() => setSettingsOpen(false)}
        aria-label={t('Close', 'Close')}
      />
    </>
  );
}
